// Renderer chunka: dwa obrazy na chunk zamiast tysięcy sprite'ów (kluczowe dla wydajności na telefonach).
//  1) grunt: grunt + drogi + niskie ściany budynków + pnie + wraki, rysowane do canvasa w połowie rozdzielczości
//  2) korony drzew: osobny obraz nad postaciami, półprzezroczysty
import Phaser from 'phaser';

import type { Chunk } from '../world/chunkCodec.js';
import { Grid } from '../world/coords.js';
import { B, G, O, R } from '../world/tiles.js';
import { DOOR, FLOOR, GROUND, OBJECT_COLORS, ROAD, WALL_LEFT, WALL_RIGHT, WALL_TOP } from './palette.js';

const HW = 8;            // połowa szerokości kafelka w canvasie (16×8 px = połowa skali sceny)
const HH = 4;
const PAD = 24;          // zapas u góry na wysokość ścian i koron
const SCALE = 2;

function shade(hex: string, k: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(c * k)));
  return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
}

function hash(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

export class ChunkRenderer {
  private images = new Map<string, Phaser.GameObjects.Image[]>();

  constructor(private scene: Phaser.Scene, private grid: Grid) {}

  add(c: Chunk): void {
    const key = `${c.cx},${c.cy}`;
    if (this.images.has(key)) this.remove(c.cx, c.cy);
    const n = c.size;
    const w = n * 2 * HW, h = n * 2 * HH + PAD;
    const ground = document.createElement('canvas');
    ground.width = w; ground.height = h;
    const canopy = document.createElement('canvas');
    canopy.width = w; canopy.height = h;
    const g = ground.getContext('2d')!;
    const k = canopy.getContext('2d')!;
    const ox = n * HW;          // wierzchołek kafelka (0,0) w canvasie
    const baseX = c.cx * n, baseY = c.cy * n;
    let trees = 0;

    const diamond = (ctx: CanvasRenderingContext2D, lx: number, ly: number, color: string, lift = 0) => {
      const x = (lx - ly) * HW + ox, y = (lx + ly) * HH + PAD - lift;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x, y); ctx.lineTo(x + HW, y + HH); ctx.lineTo(x, y + 2 * HH); ctx.lineTo(x - HW, y + HH);
      ctx.closePath(); ctx.fill();
    };

    // Kolejność rysowania po przekątnych (dalsze najpierw), żeby ściany zasłaniały to, co za nimi.
    for (let s = 0; s < 2 * n - 1; s++) {
      for (let ly = Math.max(0, s - n + 1); ly <= Math.min(n - 1, s); ly++) {
        const lx = s - ly, i = ly * n + lx;
        const noise = 0.9 + hash(baseX + lx, baseY + ly) * 0.2;
        const road = c.road[i], bld = c.building[i], obj = c.object[i];
        let col = GROUND[c.ground[i]] ?? GROUND[G.GRASS];
        if (road !== R.NONE) col = ROAD[road] ?? col;
        if (bld === B.FLOOR) col = FLOOR;
        diamond(g, lx, ly, shade(col, noise));
        if (road === R.MAJOR && (lx + ly) % 6 === 0 && hash(lx, ly) > 0.5) diamond(g, lx, ly, 'rgba(200,190,150,0.10)');
        if (road === R.RAIL || road === R.TRAM) diamond(g, lx, ly, 'rgba(120,110,100,0.35)');
        if (bld === B.WALL) {
          const x = (lx - ly) * HW + ox, y = (lx + ly) * HH + PAD, hgt = 10;
          g.fillStyle = shade(WALL_LEFT, noise);
          g.beginPath(); g.moveTo(x - HW, y + HH - hgt); g.lineTo(x, y + 2 * HH - hgt); g.lineTo(x, y + 2 * HH); g.lineTo(x - HW, y + HH); g.fill();
          g.fillStyle = shade(WALL_RIGHT, noise);
          g.beginPath(); g.moveTo(x + HW, y + HH - hgt); g.lineTo(x, y + 2 * HH - hgt); g.lineTo(x, y + 2 * HH); g.lineTo(x + HW, y + HH); g.fill();
          diamond(g, lx, ly, shade(WALL_TOP, noise), hgt);
        } else if (bld === B.DOOR) {
          diamond(g, lx, ly, DOOR);
        }
        if (obj === O.TREE) {
          const x = (lx - ly) * HW + ox, y = (lx + ly) * HH + PAD + HH;
          g.fillStyle = '#2b1f17'; g.fillRect(x - 1, y - 8, 2, 8);
          const r = 7 + hash(lx + baseX, ly + baseY) * 4;
          k.fillStyle = shade('#1f3a1c', 0.8 + hash(lx, ly + 7) * 0.4);
          k.beginPath(); k.arc(x, y - 12, r, 0, Math.PI * 2); k.fill();
          k.fillStyle = 'rgba(80,110,60,0.25)';
          k.beginPath(); k.arc(x - 2, y - 14, r * 0.55, 0, Math.PI * 2); k.fill();
          trees++;
        } else if (obj !== O.NONE) {
          const x = (lx - ly) * HW + ox, y = (lx + ly) * HH + PAD + HH;
          g.fillStyle = OBJECT_COLORS[obj] ?? '#444';
          if (obj === O.WRECK) { g.fillRect(x - 7, y - 5, 14, 6); g.fillStyle = '#222'; g.fillRect(x - 4, y - 8, 8, 3); }
          else if (obj === O.BUSH) { g.beginPath(); g.arc(x, y - 2, 4, 0, Math.PI * 2); g.fill(); }
          else if (obj === O.LAMP) { g.fillRect(x, y - 14, 1, 14); }
          else g.fillRect(x - 3, y - 3, 6, 3);
        }
      }
    }
    const [sx, sy] = this.grid.tileToScreen(baseX, baseY);
    const left = sx - ox * SCALE, top = sy - PAD * SCALE;
    const tex = this.scene.textures.addCanvas(`chunk-g-${key}`, ground)!;
    const imgs = [this.scene.add.image(left, top, tex).setOrigin(0, 0).setScale(SCALE).setDepth(-1e9)];
    if (trees) {
      const t2 = this.scene.textures.addCanvas(`chunk-k-${key}`, canopy)!;
      imgs.push(this.scene.add.image(left, top, t2).setOrigin(0, 0).setScale(SCALE).setDepth(1e9).setAlpha(0.82));
    } else {
      canopy.width = canopy.height = 0;
    }
    this.images.set(key, imgs);
  }

  remove(cx: number, cy: number): void {
    const key = `${cx},${cy}`;
    for (const img of this.images.get(key) ?? []) img.destroy();
    this.images.delete(key);
    for (const t of [`chunk-g-${key}`, `chunk-k-${key}`]) if (this.scene.textures.exists(t)) this.scene.textures.remove(t);
  }

  get count(): number { return this.images.size; }
}
